import { usePositions, type Position } from "@/hooks/usePositions";
import { formatINR, formatNum } from "@/lib/format";

function ModeChip({ mode }: { mode: "REAL" | "VIRTUAL" }) {
  const isReal = mode === "REAL";
  return (
    <span
      style={{
        display: "inline-block",
        fontSize: 10,
        fontWeight: 700,
        padding: "2px 8px",
        borderRadius: 4,
        background: isReal ? "var(--green)" : "var(--amber)",
        color: "#fff",
        letterSpacing: 0.5,
      }}
    >
      {mode}
    </span>
  );
}

export default function PositionTable({ mode }: { mode?: "REAL" | "VIRTUAL" }) {
  const { positions, isLoading, error } = usePositions(mode);

  if (isLoading) return <div style={{ padding: 20, color: "var(--text-muted)" }}>Loading positions…</div>;
  if (error) return <div style={{ padding: 20, color: "var(--red-light)" }}>Error: {error.message}</div>;
  if (!positions?.length) return <div style={{ padding: 20, color: "var(--text-muted)" }}>No open positions</div>;

  return (
    <div style={{ overflowX: "auto" }}>
      <table>
        <thead>
          <tr>
            <th>Symbol</th>
            <th>Mode</th>
            <th>Side</th>
            <th style={{ textAlign: "right" }}>Qty</th>
            <th style={{ textAlign: "right" }}>Avg Price</th>
            <th style={{ textAlign: "right" }}>LTP</th>
            <th style={{ textAlign: "right" }}>P&L</th>
            <th style={{ textAlign: "right" }}>P&L %</th>
            <th style={{ textAlign: "right" }}>Invested</th>
            <th style={{ textAlign: "right" }}>Current</th>
            <th>Strategy</th>
          </tr>
        </thead>
        <tbody>
          {positions.map((p: Position) => {
            const pnlPositive = p.pnl >= 0;
            return (
              <tr key={p._id}>
                <td style={{ fontWeight: 600 }}>{p.symbol}</td>
                <td><ModeChip mode={p.mode} /></td>
                <td style={{ color: p.side === "LONG" ? "var(--green-light)" : "var(--red-light)", fontWeight: 500 }}>
                  {p.side}
                </td>
                <td style={{ textAlign: "right", fontFamily: "var(--font-mono)" }}>{formatNum(p.qty)}</td>
                <td style={{ textAlign: "right", fontFamily: "var(--font-mono)" }}>₹{formatNum(p.avgPrice)}</td>
                <td style={{ textAlign: "right", fontFamily: "var(--font-mono)" }}>₹{formatNum(p.ltp)}</td>
                <td style={{ textAlign: "right", fontFamily: "var(--font-mono)", color: pnlPositive ? "var(--green-light)" : "var(--red-light)" }}>
                  {pnlPositive ? "+" : ""}{formatINR(p.pnl).replace("₹", "₹")}
                </td>
                <td style={{ textAlign: "right", fontFamily: "var(--font-mono)", color: pnlPositive ? "var(--green-light)" : "var(--red-light)" }}>
                  {pnlPositive ? "+" : ""}{p.pnlPct.toFixed(2)}%
                </td>
                <td style={{ textAlign: "right", fontFamily: "var(--font-mono)" }}>{formatINR(p.invested)}</td>
                <td style={{ textAlign: "right", fontFamily: "var(--font-mono)" }}>{formatINR(p.currentValue)}</td>
                <td style={{ color: "var(--text-muted)", fontSize: 12 }}>{p.strategy || "—"}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
